import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ORIGIN_LABELS,
  useDemandSummary,
  type DemandBreakdown,
  type RequestOrigin,
  type RequestStatus,
} from '@/hooks/useProductRequests'
import { fmtDate } from '@/lib/dates'
import { RequestStatusBadge } from './RequestStatusBadge'

// Same eight-in-pipeline-order / three-source ordering the rest of the module
// uses, so a reader scanning these tables sees the workflow, not a hash order.
const STATUS_ORDER: RequestStatus[] = [
  'OPEN',
  'NEED_TO_SOURCE',
  'SOURCED',
  'ADDED_TO_PO',
  'ORDERED',
  'CUSTOMER_NOTIFIED',
  'PURCHASED',
  'CLOSED_NO_PURCHASE',
]

const SOURCE_ORDER: RequestOrigin[] = ['BREWSTER', 'PHONE_EMAIL', 'EVENT']

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-2 border-b border-[var(--line-warm)] pb-1 text-xs uppercase tracking-[0.06em] text-[var(--mute)]">
        {title}
      </div>
      {children}
    </section>
  )
}

const thCls =
  'tbl-th text-left'
const tdCls = 'px-2 py-1.5 text-[var(--text-primary)]'

/** A label/count list (product type, brand) — same two-column shape as the
 *  by-status and by-source tables, but the rows come from the data. */
function BreakdownTable({
  rows,
  emptyLabel,
}: {
  rows: DemandBreakdown[]
  emptyLabel: string
}) {
  if (rows.length === 0) {
    return <p className="text-xs text-[var(--mute)]">{emptyLabel}</p>
  }
  return (
    <table className="w-full text-xs">
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className="border-b border-[var(--line-warm)]">
            <td className={tdCls}>{r.label}</td>
            <td className={`${tdCls} text-right tabular-nums`}>{r.count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function DemandSummaryModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data, isLoading, isError } = useDemandSummary(open)
  // The backend sends up to 50; 10 is the scannable buying list and the rest
  // is there for whoever wants to dig.
  const [showAllProducts, setShowAllProducts] = React.useState(false)
  const topProducts = data?.top_products ?? []
  const shownProducts = showAllProducts ? topProducts : topProducts.slice(0, 10)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] flex max-h-[85vh] w-[92vw] flex-col overflow-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-base">Demand summary</DialogTitle>
          <DialogDescription className="text-xs text-[var(--mute)]">
            What customers keep asking for, where it comes from, and what has gone
            past its follow-up date.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 overflow-y-auto pr-1">
          {isLoading && (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          )}

          {isError && !isLoading && (
            <p className="text-xs text-[var(--status-rejected-fg)]">Failed to load the demand summary</p>
          )}

          {data && !isLoading && (
            <>
              <Section title="Overdue follow-ups">
                {data.overdue_follow_ups.length === 0 ? (
                  <p className="text-xs text-[var(--mute)]">Nothing overdue</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-[var(--surface-header)]">
                      <tr>
                        <th className={thCls}>Follow up</th>
                        <th className={thCls}>Customer</th>
                        <th className={thCls}>Product</th>
                        <th className={thCls}>Assigned to</th>
                        <th className={thCls}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.overdue_follow_ups.map((r) => (
                        <tr key={r.id} className="border-b border-[var(--line-warm)]">
                          <td className="px-2 py-1.5 font-semibold tabular-nums whitespace-nowrap text-[var(--status-rejected-fg)]">
                            {fmtDate(r.follow_up_date)}
                          </td>
                          <td className={tdCls}>{r.customer_name}</td>
                          <td className={tdCls}>{r.product_title}</td>
                          <td className={tdCls}>{r.assigned_to || '—'}</td>
                          <td className="px-2 py-1.5">
                            <RequestStatusBadge status={r.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>

              <Section title="Conversion (90 days)">
                <p className="text-xs text-[var(--text-primary)]">
                  {data.conversion.purchased_90d} of {data.conversion.created_90d} requests
                  purchased — {data.conversion.rate_pct}%
                </p>
              </Section>

              <Section title="MOST REQUESTED">
                {topProducts.length === 0 ? (
                  <p className="text-xs text-[var(--mute)]">No requests logged yet</p>
                ) : (
                  <>
                    <table className="w-full text-xs">
                      <thead className="bg-[var(--surface-header)]">
                        <tr>
                          <th className={thCls}>Product</th>
                          <th className={`${thCls} text-right`}>Requests</th>
                          <th className={`${thCls} text-right`}>Total qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shownProducts.map((p) => (
                          <tr key={p.product_key} className="border-b border-[var(--line-warm)]">
                            <td className={tdCls}>{p.product_title || '(untitled)'}</td>
                            <td className={`${tdCls} text-right tabular-nums`}>
                              {p.request_count}
                            </td>
                            <td className={`${tdCls} text-right tabular-nums`}>
                              {p.total_qty}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {topProducts.length > 10 && (
                      <button
                        type="button"
                        onClick={() => setShowAllProducts((v) => !v)}
                        className="mt-2 text-xs text-[var(--brand)] hover:underline"
                      >
                        {showAllProducts
                          ? 'Show less'
                          : `Show more (${topProducts.length - 10})`}
                      </button>
                    )}
                  </>
                )}
              </Section>

              <div className="grid grid-cols-2 gap-5">
                <Section title="Requests by status">
                  <table className="w-full text-xs">
                    <tbody>
                      {STATUS_ORDER.map((s) => (
                        <tr key={s} className="border-b border-[var(--line-warm)]">
                          <td className="px-2 py-1.5">
                            <RequestStatusBadge status={s} />
                          </td>
                          <td className={`${tdCls} text-right tabular-nums`}>
                            {data.counts_by_status[s] ?? 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>

                <Section title="Requests by source">
                  <table className="w-full text-xs">
                    <tbody>
                      {SOURCE_ORDER.map((s) => (
                        <tr key={s} className="border-b border-[var(--line-warm)]">
                          <td className={tdCls}>{ORIGIN_LABELS[s]}</td>
                          <td className={`${tdCls} text-right tabular-nums`}>
                            {data.counts_by_source[s] ?? 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <Section title="Requests by Product Type">
                  <BreakdownTable
                    rows={data.by_product_type ?? []}
                    emptyLabel="No product types recorded"
                  />
                </Section>

                <Section title="Requests by Brand">
                  <BreakdownTable
                    rows={data.by_brand ?? []}
                    emptyLabel="No brands recorded"
                  />
                </Section>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
